import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer with a free-shipping progress bar.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();

    // Handle adding items to cart
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Update sticky state for the drawer summary
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    // Update free shipping bar whenever drawer opens
    this.addEventListener(DialogOpenEvent.eventName, async () => {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this.#updateFreeShippingBar(cart.total_price);
      } catch (err) {
        console.error('Free shipping bar error:', err);
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  // --- Cart add handler ---
  #handleCartAdd = async () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // Update free shipping bar when item is added
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.#updateFreeShippingBar(cart.total_price);
    } catch (err) {
      console.error('Free shipping bar error:', err);
    }
  };

  // --- Drawer open/close methods ---
  open() {
    this.showDialog();

    // Close drawer if Shopify installments CTA is clicked
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // --- Sticky state for summary ---
  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  // --- Free shipping bar method ---
  #updateFreeShippingBar(cartTotal) {
    // Only show free shipping bar to Australian visitors
    if (typeof Shopify === 'undefined' || Shopify.country !== 'AU') return;

    const drawerContent = this.shadowRoot?.querySelector('.cart-drawer__content') ||
                          this.querySelector('.cart-drawer__content');
    if (!drawerContent) return;

    let wrapper = drawerContent.querySelector('#free-shipping-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.id = 'free-shipping-wrapper';
      wrapper.style = 'margin-bottom:16px; padding:0 12px;';
      wrapper.innerHTML = `
        <p id="free-shipping-text" style="margin-bottom:6px; font-weight:500; font-size:14px;"></p>
        <div style="background:#eee; border-radius:10px; overflow:hidden; height:10px;">
          <div id="free-shipping-bar" style="height:100%; width:0%; background:#ff00a9; transition:width 0.3s ease;"></div>
        </div>
      `;
      drawerContent.prepend(wrapper);
    }

    const bar = wrapper.querySelector('#free-shipping-bar');
    const text = wrapper.querySelector('#free-shipping-text');
    if (!bar || !text) return;

    const FREE_SHIPPING_THRESHOLD = 10000; // $100 AUD
    const remaining = Math.max(FREE_SHIPPING_THRESHOLD - cartTotal, 0);
    const progress = Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100);

    bar.style.width = `${progress}%`;
    text.innerHTML = remaining <= 0
      ? '🎉 You’ve unlocked free shipping!'
      : `Only $${(remaining / 100).toFixed(2)} more to score free shipping 🐾`;
  }
}

// --- Register the component ---
if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
